import React, { useEffect, useState, useRef } from "react";
import { io } from "socket.io-client";
import AdminNavbar from './AdminNavbar';

// Fix for process not defined error
if (typeof process === 'undefined') {
  window.process = {
    env: {
      NODE_ENV: 'development'
    },
    nextTick: (callback) => setTimeout(callback, 0),
    browser: true
  };
}

// Dynamic import for simple-peer to avoid SSR issues
let Peer;
if (typeof window !== 'undefined') {
  import('simple-peer').then(module => {
    Peer = module.default;
  });
}

const socket = io("https://missionmap-lc.onrender.com");

export default function VoiceChatApp() {
  const [joinedRoom, setJoinedRoom] = useState(null);
  const [peers, setPeers] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [allUsers, setAllUsers] = useState({});
  const [screenStreams, setScreenStreams] = useState({});
  const [videoStreams, setVideoStreams] = useState({});
  const [activeScreenShare, setActiveScreenShare] = useState(null);
  const [localVideoStream, setLocalVideoStream] = useState(null);
  const [userName, setUserName] = useState(() => "User-" + Math.floor(Math.random() * 1000));
  const [isPeerLoaded, setIsPeerLoaded] = useState(false);
  
  const myStream = useRef();
  const myScreenStream = useRef();
  const myVideoStream = useRef();
  const peersRef = useRef({});
  const screenVideoRef = useRef();
  const localVideoRef = useRef();
  const socketRef = useRef();
  const token = localStorage.getItem("token");

  // Load simple-peer dynamically
  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('simple-peer').then(module => {
        window.SimplePeer = module.default;
        setIsPeerLoaded(true);
      }).catch(error => {
        console.error('Failed to load simple-peer:', error);
      });
    }
  }, []);

  // Socket connection status
  useEffect(() => {
    socketRef.current = socket;
    
    socket.on("connect", () => {
      console.log("✅ Connected to voice server");
      setConnectionStatus("connected");
    });

    socket.on("disconnect", () => {
      console.log("❌ Disconnected from voice server");
      setConnectionStatus("disconnected");
    });

    socket.on("connect_error", (error) => {
      console.error("🔌 Connection error:", error);
      setConnectionStatus("error");
    });

    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("connect_error");
    };
  }, []);

  // Fetch current user profile with profile image
  useEffect(() => {
    if (!token) return;

    // Fetch current user profile
    fetch("https://missionmap-lc.onrender.com/api/auth/me", {
      headers: { Authorization: "Bearer " + token },
    })
      .then((res) => res.json())
      .then((data) => {
        setCurrentUser(data);
        setUserName(data.username || "User");
        // Initialize current user in allUsers
        setAllUsers(prev => ({
          ...prev,
          [data._id]: {
            ...data,
            isMuted: false,
            isDeafened: false,
            isSharingScreen: false,
            hasVideo: false,
            isYou: true,
            socketId: socketRef.current?.id
          }
        }));
      })
      .catch((error) => {
        console.error("Failed to fetch user profile:", error);
      });

    // Socket listeners
    socket.on("user-joined", handleUserJoined);
    socket.on("user-left", handleUserLeft);
    socket.on("user-status-updated", handleUserStatusUpdated);
    socket.on("user-video-status-updated", handleUserVideoStatusUpdated);
    socket.on("user-screen-sharing-started", handleScreenSharingStarted);
    socket.on("user-screen-sharing-stopped", handleScreenSharingStopped);

    // Room-based socket listeners
    socket.on("all-users", handleAllUsers);
    socket.on("offer", handleOffer);
    socket.on("answer", handleAnswer);
    socket.on("user-left-room", handleUserLeftRoom);

    return () => {
      socket.off("user-joined");
      socket.off("user-left");
      socket.off("user-status-updated");
      socket.off("user-video-status-updated");
      socket.off("user-screen-sharing-started");
      socket.off("user-screen-sharing-stopped");
      socket.off("all-users");
      socket.off("offer");
      socket.off("answer");
      socket.off("user-left-room");
    };
  }, [token, isPeerLoaded]);

  // Get user display info with fallbacks
  const getUserDisplayInfo = (user) => {
    if (!user) return { username: "Unknown", profileImage: null, role: "User" };
    
    return {
      username: user.username || user.name || "Unknown User",
      profileImage: user.profileImage ? 
        user.profileImage : null,
      role: user.role || "User"
    };
  };

  // Safe Peer creation function
  const createPeerSafe = (options) => {
    if (!window.SimplePeer) {
      console.error('SimplePeer not loaded yet');
      return null;
    }
    return new window.SimplePeer(options);
  };

  // Sync peers state function
  const syncPeersState = () => {
    setPeers(
      Object.values(peersRef.current).map(({ socketId, userName, stream }) => ({
        socketId,
        userName,
        stream,
      }))
    );
  };

  // Handle all users from room system
  const handleAllUsers = (users) => {
    console.log("👥 All users in room:", users);
    
    users.forEach((user) => {
      const id = user.socketId;
      if (peersRef.current[id]) return;
      
      if (myStream.current && isPeerLoaded) {
        const peer = createPeerSafe({
          initiator: true,
          trickle: false,
          stream: getCurrentStream(),
          config: { 
            iceServers: [
              { urls: "stun:stun.l.google.com:19302" },
              { urls: "stun:global.stun.twilio.com:3478" }
            ] 
          },
        });

        if (!peer) return;

        peer.on("signal", (signal) => {
          socketRef.current.emit("offer", { 
            target: id, 
            sdp: signal, 
            caller: socketRef.current.id,
            userInfo: getCurrentUserInfo()
          });
        });

        peer.on("stream", (remoteStream) => {
          console.log("📹 Received remote stream from:", id, "User:", user.userInfo?.username);
          if (peersRef.current[id]) {
            peersRef.current[id].stream = remoteStream;
            handleRemoteStream(remoteStream, user.userInfo, id);
          }
          syncPeersState();
        });

        peer.on("close", () => {
          console.log("🔒 Peer connection closed:", id);
          delete peersRef.current[id];
          syncPeersState();
        });

        peer.on("error", (err) => {
          console.error("❌ Peer error:", err);
        });

        peersRef.current[id] = {
          socketId: id,
          peer,
          userName: user.userInfo?.username || "Remote",
          stream: null,
          userInfo: user.userInfo
        };

        // Add user to allUsers with proper identification
        if (user.userInfo?._id) {
          setAllUsers(prev => ({
            ...prev,
            [user.userInfo._id]: {
              ...user.userInfo,
              isMuted: user.userInfo.isMuted || false,
              isDeafened: user.userInfo.isDeafened || false,
              isSharingScreen: user.userInfo.isSharingScreen || false,
              hasVideo: user.userInfo.hasVideo || false,
              isYou: false,
              socketId: id
            }
          }));
        }
      }
    });
    syncPeersState();
  };

  // Handle offer from room system
  const handleOffer = ({ sdp, caller, userInfo }) => {
    console.log("📞 Received offer from:", caller, "User:", userInfo?.username);
    
    if (peersRef.current[caller] || !isPeerLoaded) return;
    
    if (myStream.current) {
      const peer = createPeerSafe({
        initiator: false,
        trickle: false,
        stream: getCurrentStream(),
        config: { 
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" },
            { urls: "stun:global.stun.twilio.com:3478" }
          ] 
        },
      });

      if (!peer) return;

      peer.on("signal", (signal) => {
        socketRef.current.emit("answer", { 
          target: caller, 
          sdp: signal, 
          responder: socketRef.current.id,
          userInfo: getCurrentUserInfo()
        });
      });

      peer.on("stream", (remoteStream) => {
        console.log("📹 Received remote stream from:", caller, "User:", userInfo?.username);
        if (peersRef.current[caller]) {
          peersRef.current[caller].stream = remoteStream;
          handleRemoteStream(remoteStream, userInfo, caller);
        }
        syncPeersState();
      });

      peer.on("close", () => {
        console.log("🔒 Peer connection closed:", caller);
        delete peersRef.current[caller];
        syncPeersState();
      });

      peer.on("error", (err) => {
        console.error("❌ Peer error:", err);
      });

      peersRef.current[caller] = {
        socketId: caller,
        peer,
        userName: userInfo?.username || "Remote",
        stream: null,
        userInfo: userInfo
      };

      // Add user to allUsers with proper identification
      if (userInfo?._id) {
        setAllUsers(prev => ({
          ...prev,
          [userInfo._id]: {
            ...userInfo,
            isMuted: userInfo.isMuted || false,
            isDeafened: userInfo.isDeafened || false,
            isSharingScreen: userInfo.isSharingScreen || false,
            hasVideo: userInfo.hasVideo || false,
            isYou: false,
            socketId: caller
          }
        }));
      }

      // Signal the peer after a short delay
      setTimeout(() => {
        if (peer && !peer.destroyed) {
          peer.signal(sdp);
        }
      }, 1000);
    }
  };

  // Handle answer from room system
  const handleAnswer = ({ sdp, responder, userInfo }) => {
    console.log("📞 Received answer from:", responder, "User:", userInfo?.username);
    
    const item = peersRef.current[responder];
    if (item && item.peer && !item.peer.destroyed) {
      item.peer.signal(sdp);
    }

    // Update user info in allUsers
    if (userInfo?._id) {
      setAllUsers(prev => ({
        ...prev,
        [userInfo._id]: {
          ...userInfo,
          isMuted: userInfo.isMuted || false,
          isDeafened: userInfo.isDeafened || false,
          isSharingScreen: userInfo.isSharingScreen || false,
          hasVideo: userInfo.hasVideo || false,
          isYou: false,
          socketId: responder
        }
      }));
    }
  };

  // Get current user info for socket events
  const getCurrentUserInfo = () => {
    return {
      username: currentUser?.username || userName,
      profileImage: currentUser?.profileImage,
      role: currentUser?.role,
      _id: currentUser?._id,
      hasVideo: hasVideo,
      isMuted: isMuted,
      isDeafened: isDeafened,
      isSharingScreen: isSharingScreen
    };
  };

  // Handle user left room
  const handleUserLeftRoom = ({ socketId }) => {
    console.log("👋 User left room:", socketId);
    
    const item = peersRef.current[socketId];
    if (item && item.peer) {
      item.peer.destroy();
    }
    
    // Remove user from allUsers
    setAllUsers(prev => {
      const newUsers = { ...prev };
      Object.keys(newUsers).forEach(userId => {
        if (newUsers[userId].socketId === socketId) {
          delete newUsers[userId];
        }
      });
      return newUsers;
    });
    
    delete peersRef.current[socketId];
    syncPeersState();
  };

  // Join voice call function
  const joinVoiceCall = async () => {
    try {
      console.log("🎤 Joining voice call...");
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 48000,
          sampleSize: 16
        },
        video: false 
      });
      
      myStream.current = stream;
      setJoinedRoom({ name: "Voice Call Room", id: "voice-room" });
      setIsMuted(false);
      setIsDeafened(false);
      setIsSharingScreen(false);
      setHasVideo(false);
      setLocalVideoStream(null);

      // Set current user info
      const userInfo = getCurrentUserInfo();

      setAllUsers(prev => ({
        ...prev,
        [currentUser?._id || socketRef.current.id]: {
          ...userInfo,
          isYou: true,
          socketId: socketRef.current.id
        }
      }));

      // Join room using the room system
      socketRef.current.emit("join-room", { 
        roomId: "voice-room", 
        userName: currentUser?.username || userName,
        userInfo: userInfo
      });

      console.log("✅ Successfully joined voice call with audio stream");

    } catch (error) {
      console.error("❌ Failed to join voice call:", error);
      if (error.name === "NotAllowedError") {
        alert("🎤 Microphone access denied. Please allow microphone permissions and try again.");
      } else if (error.name === "NotFoundError") {
        alert("🎤 No microphone found. Please check your audio devices.");
      } else {
        alert("❌ Failed to access microphone. Please check permissions and try again.");
      }
    }
  };

  // Join video call function
  const joinVideoCall = async () => {
    try {
      console.log("🎥 Joining video call...");
      
      // Request both audio and video
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 48000,
          sampleSize: 16
        },
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 24 }
        }
      });
      
      myStream.current = stream;
      myVideoStream.current = stream;
      setLocalVideoStream(stream);
      setJoinedRoom({ name: "Video Call Room", id: "video-room" });
      setIsMuted(false);
      setIsDeafened(false);
      setIsSharingScreen(false);
      setHasVideo(true);

      // Set current user info
      const userInfo = getCurrentUserInfo();

      setAllUsers(prev => ({
        ...prev,
        [currentUser?._id || socketRef.current.id]: {
          ...userInfo,
          isYou: true,
          socketId: socketRef.current.id
        }
      }));

      // Join room using the room system
      socketRef.current.emit("join-room", { 
        roomId: "video-room", 
        userName: currentUser?.username || userName,
        userInfo: userInfo
      });

      console.log("✅ Successfully joined video call with audio and video");

    } catch (error) {
      console.error("❌ Failed to join video call:", error);
      if (error.name === "NotAllowedError") {
        alert("📹 Camera or microphone access denied. Please allow permissions and try again.");
      } else if (error.name === "NotFoundError") {
        alert("📹 No camera or microphone found. Please check your devices.");
      } else {
        alert("❌ Failed to access camera or microphone. Please check permissions and try again.");
      }
    }
  };

  // Handle when new user joins
  const handleUserJoined = ({ userId, userInfo }) => {
    console.log("👤 New user joined:", userInfo?.username);
    
    if (userInfo?._id) {
      setAllUsers(prev => ({
        ...prev,
        [userInfo._id]: {
          ...userInfo,
          isMuted: userInfo.isMuted || false,
          isDeafened: userInfo.isDeafened || false,
          isSharingScreen: userInfo.isSharingScreen || false,
          hasVideo: userInfo.hasVideo || false,
          isYou: false
        }
      }));
    }
  };

  // Handle user status updates from other users
  const handleUserStatusUpdated = ({ userId, status }) => {
    console.log("🔄 User status updated:", userId, status);
    
    setAllUsers(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        ...status
      }
    }));
  };

  // Handle video status updates from other users
  const handleUserVideoStatusUpdated = ({ userId, hasVideo }) => {
    console.log("📹 User video status updated:", userId, hasVideo);
    
    setAllUsers(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        hasVideo
      }
    }));

    setVideoStreams(prev => ({
      ...prev,
      [userId]: hasVideo
    }));
  };

  // Handle screen sharing started from other users
  const handleScreenSharingStarted = ({ userId, userInfo }) => {
    console.log("🖥️ User started screen sharing:", userInfo?.username);
    
    setAllUsers(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        isSharingScreen: true
      }
    }));

    setScreenStreams(prev => ({
      ...prev,
      [userId]: true
    }));

    // Auto-view the screen share if no one else is sharing
    if (!activeScreenShare) {
      setActiveScreenShare(userId);
    }
  };

  // Handle screen sharing stopped from other users
  const handleScreenSharingStopped = ({ userId }) => {
    console.log("🖥️ User stopped screen sharing:", userId);
    
    setAllUsers(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        isSharingScreen: false
      }
    }));

    setScreenStreams(prev => {
      const newStreams = { ...prev };
      delete newStreams[userId];
      return newStreams;
    });

    if (activeScreenShare === userId) {
      setActiveScreenShare(null);
    }
  };

  // Handle user leaving
  const handleUserLeft = ({ userId }) => {
    console.log("👋 User left:", userId);
    
    // Find and clean up peer connection by userId
    let socketIdToRemove = null;
    Object.entries(peersRef.current).forEach(([socketId, peerObj]) => {
      if (peerObj.userInfo?._id === userId) {
        socketIdToRemove = socketId;
      }
    });

    if (socketIdToRemove && peersRef.current[socketIdToRemove]) {
      const peerObj = peersRef.current[socketIdToRemove];
      if (peerObj.peer && !peerObj.peer.destroyed) {
        peerObj.peer.destroy();
      }
      if (peerObj.audioElement) {
        peerObj.audioElement.remove();
      }
      if (peerObj.videoElement) {
        peerObj.videoElement.remove();
      }
      delete peersRef.current[socketIdToRemove];
    }
    
    setAllUsers(prev => {
      const newUsers = { ...prev };
      delete newUsers[userId];
      return newUsers;
    });
    
    setScreenStreams(prev => {
      const newStreams = { ...prev };
      delete newStreams[userId];
      return newStreams;
    });
    
    setVideoStreams(prev => {
      const newStreams = { ...prev };
      delete newStreams[userId];
      return newStreams;
    });
    
    if (activeScreenShare === userId) {
      setActiveScreenShare(null);
    }
    
    syncPeersState();
  };

  // Get current stream based on user's settings
  const getCurrentStream = () => {
    if (isSharingScreen && myScreenStream.current) {
      return myScreenStream.current;
    }
    if (hasVideo && myVideoStream.current) {
      return myVideoStream.current;
    }
    return myStream.current;
  };

  // Handle remote stream (audio + video)
  const handleRemoteStream = (stream, userInfo, socketId) => {
    console.log("📹 Handling remote stream from:", userInfo?.username);
    
    const videoTracks = stream.getVideoTracks();
    const audioTracks = stream.getAudioTracks();
    
    console.log("🎯 Stream tracks - Video:", videoTracks.length, "Audio:", audioTracks.length);

    // Always create audio element for voice
    if (audioTracks.length > 0) {
      const audioElement = createAudioElement(stream, userInfo);
      if (!peersRef.current[socketId]) {
        peersRef.current[socketId] = { userInfo };
      }
      peersRef.current[socketId].audioElement = audioElement;
      
      if (isDeafened) {
        audioElement.muted = true;
      }
    }

    // Create video element if it has video tracks
    if (videoTracks.length > 0) {
      console.log("📹 Creating video element for user:", userInfo?.username);
      createVideoElementForUser(stream, userInfo, socketId);
      
      // Update UI to show video status
      setVideoStreams(prev => ({
        ...prev,
        [userInfo?._id || socketId]: true
      }));
    }

    syncPeersState();
  };

  // Create audio element for remote stream
  const createAudioElement = (stream, userInfo) => {
    const audio = document.createElement('audio');
    audio.srcObject = stream;
    audio.autoplay = true;
    audio.playsInline = true;
    
    audio.setAttribute('playsinline', 'true');
    audio.setAttribute('webkit-playsinline', 'true');
    
    audio.onloadedmetadata = () => {
      console.log("🔊 Audio metadata loaded for:", userInfo?.username);
      audio.play().catch(e => console.log("Audio play failed:", e));
    };
    
    audio.onerror = (error) => {
      console.error("❌ Audio element error:", error);
    };
    
    audio.style.display = 'none';
    document.body.appendChild(audio);
    
    return audio;
  };

  // Create video element for user (not screen share)
  const createVideoElementForUser = (stream, userInfo, socketId) => {
    // Remove existing video element if any
    const existingVideo = peersRef.current[socketId]?.videoElement;
    if (existingVideo) {
      existingVideo.remove();
    }

    const video = document.createElement('video');
    video.srcObject = stream;
    video.autoplay = true;
    video.playsInline = true;
    video.controls = false;
    video.muted = false;
    
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.borderRadius = '8px';
    video.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
    video.style.backgroundColor = '#000';
    video.style.objectFit = 'cover';
    
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    
    video.onloadedmetadata = () => {
      console.log("📹 Video metadata loaded for user:", userInfo?.username);
      video.play().catch(e => console.log("Video play failed:", e));
    };
    
    video.onerror = (error) => {
      console.error("❌ Video element error:", error);
    };

    // Store video element
    if (!peersRef.current[socketId]) {
      peersRef.current[socketId] = { userInfo };
    }
    peersRef.current[socketId].videoElement = video;
    
    return video;
  };

  // 🔹 FIXED: Toggle video function - Now switches between audio-only and video streams
  const toggleVideo = async () => {
    const newVideoState = !hasVideo;
    
    try {
      if (newVideoState) {
        // Turning video ON - Get video stream
        console.log("📹 Turning video ON...");
        
        const videoStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: 48000,
            sampleSize: 16
          },
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            frameRate: { ideal: 24 }
          }
        });
        
        // Stop old audio stream
        if (myStream.current) {
          myStream.current.getTracks().forEach(track => track.stop());
        }
        
        // Set new stream with video
        myStream.current = videoStream;
        myVideoStream.current = videoStream;
        setLocalVideoStream(videoStream);
        setHasVideo(true);
        
        // Update local video display
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = videoStream;
        }
        
        // Replace tracks in all peer connections
        Object.values(peersRef.current).forEach(({ peer }) => {
          if (peer && !peer.destroyed) {
            try {
              // Replace both audio and video tracks
              const videoSender = peer._pc
                .getSenders()
                .find(s => s.track && s.track.kind === "video");
              const audioSender = peer._pc
                .getSenders()
                .find(s => s.track && s.track.kind === "audio");
              
              if (videoSender && videoStream.getVideoTracks().length > 0) {
                videoSender.replaceTrack(videoStream.getVideoTracks()[0]);
              }
              if (audioSender && videoStream.getAudioTracks().length > 0) {
                audioSender.replaceTrack(videoStream.getAudioTracks()[0]);
              }
            } catch (error) {
              console.error("❌ Error updating peer tracks for video:", error);
            }
          }
        });
        
      } else {
        // Turning video OFF - Switch to audio-only
        console.log("📹 Turning video OFF...");
        
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: 48000,
            sampleSize: 16
          },
          video: false
        });
        
        // Stop old video stream
        if (myStream.current) {
          myStream.current.getTracks().forEach(track => track.stop());
        }
        
        // Set new audio-only stream
        myStream.current = audioStream;
        myVideoStream.current = null;
        setLocalVideoStream(null);
        setHasVideo(false);
        
        // Clear local video display
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = null;
        }
        
        // Replace tracks in all peer connections (remove video track)
        Object.values(peersRef.current).forEach(({ peer }) => {
          if (peer && !peer.destroyed) {
            try {
              const videoSender = peer._pc
                .getSenders()
                .find(s => s.track && s.track.kind === "video");
              const audioSender = peer._pc
                .getSenders()
                .find(s => s.track && s.track.kind === "audio");
              
              // Remove video track
              if (videoSender) {
                videoSender.replaceTrack(null);
              }
              // Update audio track
              if (audioSender && audioStream.getAudioTracks().length > 0) {
                audioSender.replaceTrack(audioStream.getAudioTracks()[0]);
              }
            } catch (error) {
              console.error("❌ Error updating peer tracks for audio-only:", error);
            }
          }
        });
      }
      
      // Update user info
      setAllUsers(prev => ({
        ...prev,
        [currentUser?._id || socketRef.current.id]: {
          ...prev[currentUser?._id || socketRef.current.id],
          hasVideo: newVideoState
        }
      }));

      // Notify other users
      socket.emit("user-video-status-update", {
        userId: currentUser?._id || socketRef.current.id,
        hasVideo: newVideoState
      });

      console.log(newVideoState ? "✅ Video turned ON" : "✅ Video turned OFF");
      
    } catch (error) {
      console.error("❌ Failed to toggle video:", error);
      alert("Failed to access camera. Please check permissions and try again.");
      // Revert the state if failed
      setHasVideo(!newVideoState);
    }
  };

  // 🔹 SCREEN SHARE FUNCTION
  const toggleScreenShare = async () => {
    if (!isSharingScreen) {
      try {
        console.log("🖥️ Starting screen share...");
        
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        });
        
        myScreenStream.current = stream;
        setIsSharingScreen(true);

        // Update user info
        setAllUsers(prev => ({
          ...prev,
          [currentUser?._id || socketRef.current.id]: {
            ...prev[currentUser?._id || socketRef.current.id],
            isSharingScreen: true
          }
        }));

        setActiveScreenShare(currentUser?._id || socketRef.current.id);

        // Notify other users
        socket.emit("user-screen-sharing-started", {
          userId: currentUser?._id || socketRef.current.id,
          userInfo: getCurrentUserInfo()
        });

        // Replace video track in each peer connection
        Object.values(peersRef.current).forEach(({ peer }) => {
          if (peer && !peer.destroyed) {
            try {
              const videoSender = peer._pc
                .getSenders()
                .find(s => s.track && s.track.kind === "video");
              const audioSender = peer._pc
                .getSenders()
                .find(s => s.track && s.track.kind === "audio");
              
              if (videoSender) {
                videoSender.replaceTrack(stream.getVideoTracks()[0]);
              }
              if (audioSender && stream.getAudioTracks().length > 0) {
                audioSender.replaceTrack(stream.getAudioTracks()[0]);
              }
            } catch (error) {
              console.error("❌ Error updating peer tracks:", error);
            }
          }
        });

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        // Handle screen share ending
        stream.getVideoTracks()[0].onended = () => {
          console.log("🖥️ Screen share track ended");
          stopScreenShare();
        };

        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length > 0) {
          audioTracks[0].onended = () => {
            console.log("🔊 Screen share audio track ended");
            stopScreenShare();
          };
        }

        console.log("✅ Screen sharing started");

      } catch (error) {
        console.error("❌ Failed to start screen share:", error);
        if (error.name !== "NotAllowedError") {
          alert("Failed to share screen. Please check permissions and try again.");
        }
      }
    } else {
      stopScreenShare();
    }
  };

  // 🔹 STOP SCREEN SHARE FUNCTION
  const stopScreenShare = () => {
    console.log("🖥️ Stopping screen share...");

    if (myScreenStream.current) {
      myScreenStream.current.getTracks().forEach(track => track.stop());
      myScreenStream.current = null;
    }

    setIsSharingScreen(false);
    
    if (activeScreenShare === (currentUser?._id || socketRef.current.id)) {
      setActiveScreenShare(null);
    }

    // Update user info
    setAllUsers(prev => ({
      ...prev,
      [currentUser?._id || socketRef.current.id]: {
        ...prev[currentUser?._id || socketRef.current.id],
        isSharingScreen: false
      }
    }));

    // Notify other users
    socket.emit("user-screen-sharing-stopped", {
      userId: currentUser?._id || socketRef.current.id
    });

    // Restore original stream
    const currentStream = getCurrentStream();
    if (currentStream) {
      Object.values(peersRef.current).forEach(({ peer }) => {
        if (peer && !peer.destroyed) {
          try {
            const videoSender = peer._pc
              .getSenders()
              .find(s => s.track && s.track.kind === "video");
            const audioSender = peer._pc
              .getSenders()
              .find(s => s.track && s.track.kind === "audio");
            
            if (videoSender && currentStream.getVideoTracks().length > 0) {
              videoSender.replaceTrack(currentStream.getVideoTracks()[0]);
            } else if (videoSender) {
              videoSender.replaceTrack(null);
            }
            if (audioSender && currentStream.getAudioTracks().length > 0) {
              audioSender.replaceTrack(currentStream.getAudioTracks()[0]);
            }
          } catch (error) {
            console.error("❌ Error reverting peer tracks:", error);
          }
        }
      });

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = currentStream;
      }
    }

    console.log("✅ Screen sharing stopped");
  };

  // Toggle mute function
  const toggleMute = () => {
    if (myStream.current) {
      const audioTracks = myStream.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = isMuted; 
      });
      
      const newMutedState = !isMuted;
      setIsMuted(newMutedState);
      
      setAllUsers(prev => ({
        ...prev,
        [currentUser?._id || socketRef.current.id]: {
          ...prev[currentUser?._id || socketRef.current.id],
          isMuted: newMutedState
        }
      }));
      
      socket.emit("user-status-update", {
        userId: currentUser?._id || socketRef.current.id,
        status: { isMuted: newMutedState }
      });
      
      console.log(newMutedState ? "🔇 You muted" : "🔊 You unmuted");
    }
  };

  // Toggle deafen function
  const toggleDeafen = () => {
    const newDeafenedState = !isDeafened;
    setIsDeafened(newDeafenedState);
    
    Object.values(peersRef.current).forEach(peerObj => {
      if (peerObj.audioElement) {
        peerObj.audioElement.muted = newDeafenedState;
      }
    });
    
    setAllUsers(prev => ({
      ...prev,
      [currentUser?._id || socketRef.current.id]: {
        ...prev[currentUser?._id || socketRef.current.id],
        isDeafened: newDeafenedState
      }
    }));
    
    socket.emit("user-status-update", {
      userId: currentUser?._id || socketRef.current.id,
      status: { isDeafened: newDeafenedState }
    });
    
    console.log(newDeafenedState ? "🎧 You deafened" : "🎧 You undeafened");
  };

  // View specific user's screen
  const viewUserScreen = (userId) => {
    setActiveScreenShare(userId);
    
    const peerEntry = Object.values(peersRef.current).find(
      peer => peer.userInfo?._id === userId
    );
    
    if (peerEntry && peerEntry.videoElement) {
      updateScreenShareDisplay(userId, peerEntry.videoElement);
    } else {
      const container = document.getElementById('screen-share-container');
      if (container) {
        container.innerHTML = `
          <div class="text-white text-center p-8">
            <div class="text-lg font-semibold mb-2">Waiting for screen share...</div>
            <div class="text-gray-400">${allUsers[userId]?.username} is sharing their screen</div>
            <button class="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded" onclick="window.stopViewingScreen()">
              Stop Viewing
            </button>
          </div>
        `;
      }
    }
  };

  // Update screen share display
  const updateScreenShareDisplay = (userId, videoElement) => {
    const container = document.getElementById('screen-share-container');
    if (container) {
      container.innerHTML = '';
      
      const infoDiv = document.createElement('div');
      infoDiv.className = 'text-white mb-2';
      infoDiv.innerHTML = `
        <div class="flex justify-between items-center">
          <div class="font-semibold">
            Screen Share - ${allUsers[userId]?.username || 'User'}
          </div>
          <button id="stop-viewing-btn" class="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm">
            Stop Viewing
          </button>
        </div>
      `;
      container.appendChild(infoDiv);
      
      container.appendChild(videoElement);
      
      const stopBtn = document.getElementById('stop-viewing-btn');
      if (stopBtn) {
        stopBtn.onclick = stopViewingScreen;
      }
    }
  };

  // Stop viewing screen
  const stopViewingScreen = () => {
    setActiveScreenShare(null);
  };

  // Leave call function
  const leaveCall = () => {
    console.log("🚪 Leaving call");
    
    if (isSharingScreen) {
      stopScreenShare();
    }
    
    if (hasVideo) {
      if (myVideoStream.current) {
        myVideoStream.current.getTracks().forEach(track => track.stop());
        myVideoStream.current = null;
      }
      setLocalVideoStream(null);
      setHasVideo(false);
    }
    
    if (socketRef.current && joinedRoom) {
      socketRef.current.emit("leave-room", { 
        roomId: joinedRoom?.id || "default-room" 
      });
    }

    Object.values(peersRef.current).forEach((entry) => {
      if (entry.peer) entry.peer.destroy();
      if (entry.audioElement) entry.audioElement.remove();
      if (entry.videoElement) entry.videoElement.remove();
    });
    
    peersRef.current = {};
    setPeers({});
    setAllUsers({});
    setScreenStreams({});
    setVideoStreams({});
    setActiveScreenShare(null);
    
    if (myStream.current) {
      myStream.current.getTracks().forEach(track => track.stop());
      myStream.current = null;
    }
    if (myScreenStream.current) {
      myScreenStream.current.getTracks().forEach(track => track.stop());
      myScreenStream.current = null;
    }
    
    setJoinedRoom(null);
    setIsMuted(false);
    setIsDeafened(false);
    setIsSharingScreen(false);
    setHasVideo(false);
    setLocalVideoStream(null);
    console.log("✅ Call left successfully");
  };

  // Profile image component
  const ProfileImage = ({ user, size = "medium" }) => {
    const displayInfo = getUserDisplayInfo(user);
    const sizeClasses = {
      small: "w-8 h-8",
      medium: "w-10 h-10",
      large: "w-16 h-16"
    };

    if (displayInfo.profileImage) {
      return (
        <img
          src={displayInfo.profileImage}
          alt={displayInfo.username}
          className={`${sizeClasses[size]} rounded-full object-cover border-2 border-blue-500`}
        />
      );
    }

    return (
      <div className={`${sizeClasses[size]} bg-blue-500 rounded-full flex items-center justify-center text-white font-bold`}>
        <span className="text-sm">
          {displayInfo.username?.charAt(0).toUpperCase() || 'U'}
        </span>
      </div>
    );
  };

  // Make stopViewingScreen available globally for inline onclick
  useEffect(() => {
    window.stopViewingScreen = stopViewingScreen;
    return () => {
      delete window.stopViewingScreen;
    };
  }, []);

  // Update local video when stream changes
  useEffect(() => {
    if (localVideoRef.current && localVideoStream) {
      localVideoRef.current.srcObject = localVideoStream;
    }
  }, [localVideoStream]);

  const currentUserInfo = getUserDisplayInfo(currentUser);

  // Render user card
  const renderUserCard = (userId, user) => {
    const userInfo = getUserDisplayInfo(user);
    const hasUserVideo = videoStreams[userId] && !user.isSharingScreen;
    const isCurrentUser = user.isYou;
    
    return (
      <div key={userId} className={`bg-white rounded-lg shadow-md p-4 text-center border-2 ${
        isCurrentUser ? 'border-green-500' : 'border-gray-200'
      } ${user.isSharingScreen ? 'ring-2 ring-purple-500' : ''} ${
        hasUserVideo ? 'ring-2 ring-blue-500' : ''
      }`}>
        
        <div className="relative w-full h-48 bg-gray-800 rounded-lg overflow-hidden">
          {isCurrentUser && localVideoStream ? (
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
          ) : hasUserVideo ? (
            <div 
              className="w-full h-full"
              ref={el => {
                if (el) {
                  const peerEntry = Object.values(peersRef.current).find(
                    peer => peer.userInfo?._id === userId
                  );
                  if (peerEntry?.videoElement && !el.contains(peerEntry.videoElement)) {
                    el.innerHTML = '';
                    el.appendChild(peerEntry.videoElement);
                  }
                }
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ProfileImage user={user} size="large" />
            </div>
          )}
          
          <div className="absolute top-2 right-2">
            <span className="inline-block w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
          </div>

          <div className="absolute bottom-2 left-2 right-2 bg-black bg-opacity-50 text-white text-sm p-1 rounded">
            {userInfo.username} {isCurrentUser ? "(You)" : ""}
          </div>
        </div>

        <div className="text-sm text-gray-500 mt-2">{userInfo.role}</div>
        
        <div className="flex justify-center space-x-1 mt-2">
          {user.isMuted && <span className="bg-red-500 text-white px-2 py-1 rounded text-xs">Muted</span>}
          {user.isDeafened && <span className="bg-orange-500 text-white px-2 py-1 rounded text-xs">Deafened</span>}
          {user.isSharingScreen && (
            <span className="bg-purple-500 text-white px-2 py-1 rounded text-xs flex items-center">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
              </svg>
              Screen
            </span>
          )}
          {(hasUserVideo || (isCurrentUser && hasVideo)) && (
            <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs flex items-center">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/>
              </svg>
              Video
            </span>
          )}
        </div>

        {user.isSharingScreen && !isCurrentUser && (
          <button
            onClick={() => viewUserScreen(userId)}
            className={`w-full px-3 py-2 rounded text-sm mt-2 ${
              activeScreenShare === userId 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            {activeScreenShare === userId ? 'Currently Viewing' : 'View Screen'}
          </button>
        )}
      </div>
    );
  };

  // Show loading if Peer is not loaded yet
  if (!isPeerLoaded) {
    return (
      <>
        <AdminNavbar />
        <div className="flex h-screen bg-gray-100 items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading voice chat...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavbar />
      
      <div className="flex h-screen bg-gray-100 voice-chat-app">
        
        <div className="w-1/4 bg-gray-800 text-white p-4 flex flex-col mt-16">
          <h2 className="text-xl font-bold mb-4">Voice Chat</h2>
          
          {currentUser && (
            <div className="flex items-center space-x-3 mb-4 p-3 bg-gray-700 rounded-lg">
              <ProfileImage user={currentUser} size="medium" />
              <div className="flex-1">
                <div className="font-medium">{currentUserInfo.username}</div>
                <div className="text-xs text-gray-300">{currentUserInfo.role}</div>
              </div>
            </div>
          )}

          <div className="mt-8 flex-1">
            <h3 className="font-semibold mb-3">Connected Users ({Object.keys(allUsers).length})</h3>
            <div className="space-y-2">
              {Object.entries(allUsers).map(([userId, user]) => {
                const userInfo = getUserDisplayInfo(user);
                const isSharing = user.isSharingScreen;
                const hasUserVideo = videoStreams[userId];
                
                return (
                  <div key={userId} className="flex items-center space-x-3 p-2 bg-gray-700 rounded">
                    <ProfileImage user={user} size="small" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">
                        {userInfo.username} {user.isYou ? "(You)" : ""}
                      </div>
                      <div className="text-xs text-gray-300">{userInfo.role}</div>
                    </div>
                    <div className="flex space-x-1">
                      {user.isMuted && <span className="bg-red-500 text-white px-1 py-0.5 rounded text-xs">Muted</span>}
                      {user.isDeafened && <span className="bg-orange-500 text-white px-1 py-0.5 rounded text-xs">Deafened</span>}
                      {isSharing && <span className="bg-purple-500 text-white px-1 py-0.5 rounded text-xs">Sharing</span>}
                      {(hasUserVideo || (user.isYou && hasVideo)) && <span className="bg-blue-500 text-white px-1 py-0.5 rounded text-xs">Video</span>}
                    </div>
                    {isSharing && !user.isYou && (
                      <button
                        onClick={() => viewUserScreen(userId)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs"
                      >
                        {activeScreenShare === userId ? "Viewing" : "View"}
                      </button>
                    )}
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  </div>
                );
              })}

              {joinedRoom && Object.keys(allUsers).length === 0 && (
                <div className="text-center text-gray-400 py-4">
                  <p>Waiting for others to join...</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col bg-gray-50 mt-16">
          {joinedRoom ? (
            <>
              <div className="bg-white border-b p-4 shadow-sm">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">
                      {hasVideo ? 'Video Call' : 'Voice Call'}
                    </h2>
                    <p className="text-gray-600 text-sm">Live {hasVideo ? 'video' : 'voice'} chat session</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                      {Object.keys(allUsers).length} users connected
                    </span>
                    {Object.keys(videoStreams).length > 0 && (
                      <span className="text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                        {Object.keys(videoStreams).length} video streams
                      </span>
                    )}
                    {Object.keys(screenStreams).length > 0 && (
                      <span className="text-purple-600 bg-purple-100 px-3 py-1 rounded-full">
                        {Object.keys(screenStreams).length} screen sharing
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {activeScreenShare && (
                <div className="bg-black p-4 flex-1 flex flex-col">
                  <div 
                    ref={screenVideoRef}
                    id="screen-share-container"
                    className="flex-1 flex items-center justify-center"
                  >
                    <div className="text-white text-center">
                      <div className="text-lg font-semibold mb-2">Loading screen share...</div>
                      <div className="text-gray-400">
                        {allUsers[activeScreenShare]?.username} is sharing their screen
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={`p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-y-auto ${activeScreenShare ? 'hidden' : 'flex-1'}`}>
                {Object.entries(allUsers).map(([userId, user]) => 
                  renderUserCard(userId, user)
                )}
              </div>

              <div className="bg-gray-800 text-white p-4">
                <div className="flex justify-center items-center space-x-6">
                  <button
                    onClick={toggleMute}
                    className={`p-4 rounded-full transition-colors ${
                      isMuted 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                    title={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.784L4.728 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.728l3.655-2.784a1 1 0 011.617.784zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>

                  <button
                    onClick={toggleDeafen}
                    className={`p-4 rounded-full transition-colors ${
                      isDeafened 
                        ? 'bg-orange-600 hover:bg-orange-700' 
                        : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                    title={isDeafened ? 'Undeafen' : 'Deafen'}
                  >
                    {isDeafened ? (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.784L4.728 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.728l3.655-2.784a1 1 0 011.617.784zM11 3a1 1 0 011 1v12a1 1 0 01-1.617.784L6.728 14H4a1 1 0 01-1-1V7a1 1 0 011-1h2.728l3.655-2.784A1 1 0 0111 3z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.784L4.728 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.728l3.655-2.784a1 1 0 011.617.784zM11 3a1 1 0 011 1v12a1 1 0 01-1.617.784L6.728 14H4a1 1 0 01-1-1V7a1 1 0 011-1h2.728l3.655-2.784A1 1 0 0111 3z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>

                  <button
                    onClick={toggleVideo}
                    className={`p-4 rounded-full transition-colors ${
                      hasVideo 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                    title={hasVideo ? 'Turn off video' : 'Turn on video'}
                  >
                    {hasVideo ? (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/>
                      </svg>
                    ) : (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/>
                      </svg>
                    )}
                  </button>

                  <button
                    onClick={toggleScreenShare}
                    className={`p-4 rounded-full transition-colors ${
                      isSharingScreen 
                        ? 'bg-purple-600 hover:bg-purple-700' 
                        : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                    title={isSharingScreen ? 'Stop Sharing' : 'Share Screen'}
                  >
                    {isSharingScreen ? (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>

                  <button
                    onClick={leaveCall}
                    className="p-4 rounded-full bg-red-600 hover:bg-red-500 transition-colors"
                    title="Leave Call"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <svg className="w-24 h-24 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
                <h2 className="text-2xl mb-2">Ready to Connect?</h2>
                <p className="text-lg mb-6">Choose your call type</p>
                
                <div className="flex space-x-4 justify-center">
                  <button
                    onClick={joinVoiceCall}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold flex items-center"
                  >
                    <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                    </svg>
                    Voice Call
                  </button>
                  
                  <button
                    onClick={joinVideoCall}
                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold flex items-center"
                  >
                    <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/>
                    </svg>
                    Video Call
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}